import React, { useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { authService } from '../../../../services/api';
import { LoginErrorResponse } from '../../../../types/auth';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    padding: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: theme.spacing(2),
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    backdropFilter: 'blur(8px)',
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const LogoIcon = styled(AdminPanelSettingsIcon)(({ theme }) => ({
    fontSize: 48,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(1),
}));

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        transition: 'all 0.3s ease',
        '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.secondary.main,
            },
        },
        '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.palette.secondary.main,
            },
        },
    },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontSize: '1.1rem',
    fontWeight: 600,
    backgroundColor: theme.palette.secondary.main,
    boxShadow: `0 4px 14px ${alpha(theme.palette.secondary.main, 0.4)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        transform: 'translateY(-2px)',
        boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.6)}`,
    },
}));

const LanguageButton = styled(Button)(({ theme }) => ({
    position: 'absolute',
    top: 16,
    right: 16,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(8px)',
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 1),
    },
}));

interface LoginFormProps {
    onLoginSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await authService.adminLogin({ email, password });
            onLoginSuccess();
        } catch (err) {
            const error = err as LoginErrorResponse;
            setError(error.message || t('auth.login.errors.invalidCredentials'));
        } finally {
            setLoading(false);
        }
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
        document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    };

    return (
        <PageContainer>
            <Container component="main" maxWidth="xs">
                <StyledPaper elevation={3}>
                    <LanguageButton
                        onClick={toggleLanguage}
                        variant="outlined"
                        size="small"
                    >
                        {t('common.language')} ({i18n.language.toUpperCase()})
                    </LanguageButton>

                    <LogoContainer>
                        <LogoIcon />
                        <Typography component="h1" variant="h4" fontWeight="bold" color="secondary">
                            {t('admin.auth.login.title')}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary" align="center">
                            {t('admin.auth.login.subtitle')}
                        </Typography>
                    </LogoContainer>

                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                width: '100%', 
                                mt: 2,
                                borderRadius: 1,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <StyledTextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={t('admin.auth.login.email')}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            error={!!error}
                            disabled={loading}
                        />
                        <StyledTextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={t('admin.auth.login.password')}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            error={!!error}
                            disabled={loading}
                        />
                        <SubmitButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            disabled={loading}
                            size={isMobile ? 'large' : 'medium'}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t('admin.auth.login.submit')
                            )}
                        </SubmitButton>
                    </Form>
                </StyledPaper>
            </Container>
        </PageContainer>
    );
}; 